/** Catalogue Governance (Demo Mode only) - single governance hub for catalogue price
 * changes, substitutions, off-contract buying, and data quality. Gated via the "demo"
 * status on its route in App.tsx (AuthGuard status="demo") - only visible with Demo Mode
 * enabled. All tabs read from CatalogueGovernanceClient (in-memory mock, no backend yet -
 * see src/app/services/api/catalogueGovernance). */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab, Tabs } from '@mui/material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import OverviewTab from './components/OverviewTab';
import PriceChangesTab from './components/PriceChangesTab';
import AvailabilitySubstitutionsTab from './components/AvailabilitySubstitutionsTab';
import OffContractRequestsTab from './components/OffContractRequestsTab';
import DataQualityTab from './components/DataQualityTab';
import DuplicateProductsTab from './components/DuplicateProductsTab';
import BlockedItemsTab from './components/BlockedItemsTab';
import SummaryTiles from './components/SummaryTiles';

type CatalogueGovernanceTab =
  | 'overview'
  | 'priceChanges'
  | 'availabilitySubstitutions'
  | 'offContractRequests'
  | 'dataQuality'
  | 'duplicateProducts'
  | 'blockedItems';

const CatalogueGovernance = () => {
  const { t } = useTranslation('catalogueGovernance');
  const [activeTab, setActiveTab] = useState<CatalogueGovernanceTab>('overview');

  const TABS: { value: CatalogueGovernanceTab; label: string }[] = [
    { value: 'overview', label: t('index.tabs.overview', 'Overview') },
    { value: 'priceChanges', label: t('index.tabs.priceChanges', 'Price Changes') },
    { value: 'availabilitySubstitutions', label: t('index.tabs.availabilitySubstitutions', 'Availability & Substitutions') },
    { value: 'offContractRequests', label: t('index.tabs.offContractRequests', 'Off-contract Requests') },
    { value: 'dataQuality', label: t('index.tabs.dataQuality', 'Data Quality') },
    { value: 'duplicateProducts', label: t('index.tabs.duplicateProducts', 'Duplicate Products') },
    { value: 'blockedItems', label: t('index.tabs.blockedItems', 'Blocked Items') },
  ];

  return (
    <PageArea>
      <PageTitle subTitle={t('index.pageSubtitle', 'Single governance hub for catalogue issues, price changes, substitutions, off-contract buying and data quality')}>
        {t('index.pageTitle', 'Governance')}
      </PageTitle>

      <SummaryTiles />

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TABS.map((tab) => (
          <Tab key={tab.value}
            label={tab.label}
            value={tab.value} />
        ))}
      </Tabs>

      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'priceChanges' && <PriceChangesTab />}
      {activeTab === 'availabilitySubstitutions' && <AvailabilitySubstitutionsTab />}
      {activeTab === 'offContractRequests' && <OffContractRequestsTab />}
      {activeTab === 'dataQuality' && <DataQualityTab />}
      {activeTab === 'duplicateProducts' && <DuplicateProductsTab />}
      {activeTab === 'blockedItems' && <BlockedItemsTab />}
    </PageArea>
  );
};

export default CatalogueGovernance;
